import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import "./ApproveCandidates.css";
import { toast } from "react-hot-toast";
import { X } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { renderAsync } from "docx-preview";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ApproveProfileUpdates = () => {
  const [profileUpdates, setProfileUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [adminComment, setAdminComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [versionDetails, setVersionDetails] = useState(null);
  const [viewMode, setViewMode] = useState("table");

  // PDF/DOCX viewer state
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [cvType, setCvType] = useState(null);
  const [currentBlob, setCurrentBlob] = useState(null);
  const docxRef = useRef(null);

  const cap = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

  const stats = useMemo(() => {
    if (!profileUpdates.length) {
      return null;
    }

    const ordered = [...profileUpdates].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );

    const latest = ordered[0];
    const longestPending = ordered[ordered.length - 1];

    return {
      total: profileUpdates.length,
      latestSubmittedAt: latest
        ? new Date(latest.created_at).toLocaleString()
        : null,
      longestPendingAge: longestPending
        ? Math.max(
          0,
          Math.round(
            (Date.now() - new Date(longestPending.created_at).getTime()) /
            (1000 * 60 * 60 * 24),
          ),
        )
        : 0,
    };
  }, [profileUpdates]);

  useEffect(() => {
    fetchProfileUpdates();
  }, []);

  const fetchProfileUpdates = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/profile-versions/pending`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch profile updates");
      }

      const data = await response.json();
      setProfileUpdates(data);
    } catch (error) {
      setError("Failed to fetch profile updates");
      console.error("Error fetching profile updates:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVersionDetails = async (updateId) => {
    try {
      setDetailsLoading(true);
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/profile-versions/${updateId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch profile version details");
      }

      const data = await response.json();
      setVersionDetails(data);
    } catch (err) {
      console.error("Error loading version details:", err);
      setError("Failed to load version details");
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (showPdfViewer && cvType === "docx" && currentBlob && docxRef.current) {
      renderAsync(currentBlob, docxRef.current).catch((err) => {
        console.error("Error rendering DOCX:", err);
        toast.error("Failed to render DOCX preview");
      });
    }
  }, [showPdfViewer, cvType, currentBlob]);

  const handleViewNewCV = async (userId, filename) => {
    try {
      console.log(
        "Starting CV view for userId:",
        userId,
        "filename:",
        filename,
      );
      setPdfLoading(true);
      setShowPdfViewer(true);
      setPageNumber(1);
      setCvType(null);

      const token = localStorage.getItem("adminToken");
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3500";
      const fullUrl = `${apiUrl}/api/admin/candidates/${userId}/download-new-cv`;

      console.log("Fetching NEW CV from:", fullUrl);

      const response = await fetch(fullUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        console.log("Blob received:", blob.size, "bytes, type:", blob.type);
        setCurrentBlob(blob);

        // ALWAYS trust filename extension over Content-Type (backend often returns wrong type)
        const filenameLower = filename?.toLowerCase() || "";
        console.log("Filename extension check:", filenameLower);

        if (filenameLower.endsWith(".docx")) {
          console.log("Detected DOCX file by extension, setting cvType='docx'");
          setCvType("docx");
        } else if (filenameLower.endsWith(".pdf")) {
          console.log("Detected PDF file by extension, setting cvType='pdf'");
          setCvType("pdf");
        } else if (blob.type.includes("word") || blob.type.includes("officedocument.wordprocessingml.document")) {
          console.log("Detected DOCX file by blob type, setting cvType='docx'");
          setCvType("docx");
        } else {
          console.log("No match found, defaulting to PDF, setting cvType='pdf'");
          setCvType("pdf"); // default
        }

        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
      } else {
        const errorText = await response.text();
        console.error(
          "Failed to load CV. Status:",
          response.status,
          "Error:",
          errorText,
        );
        toast.error(`Failed to load CV: ${response.status}`);
        setShowPdfViewer(false);
      }
    } catch (error) {
      console.error("Error loading CV:", error);
      toast.error(`Error loading CV: ${error.message}`);
      setShowPdfViewer(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClosePdfViewer = () => {
    setShowPdfViewer(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setCurrentBlob(null);
    setCvType(null);
    setNumPages(null);
    setPageNumber(1);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  const handleViewDetails = (update) => {
    setSelectedUpdate(update);
    setIsModalOpen(true);
    setAdminComment("");
    fetchVersionDetails(update.id);
  };

  const handleApprove = async () => {
    if (!selectedUpdate) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/profile-versions/${selectedUpdate.id}/approve`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ adminComment }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to approve profile update");
      }

      setIsModalOpen(false);
      setSelectedUpdate(null);
      setVersionDetails(null);
      fetchProfileUpdates();
    } catch (error) {
      console.error("Error approving profile update:", error);
      toast.error("Failed to approve profile update");
    } finally {
      setActionLoading(false);
    }
  };

  function formatDate(dateStr) {
    if (!dateStr) return "";

    // If already in MM/YYYY format → return as is
    if (/^\d{2}\/\d{4}$/.test(dateStr.trim())) {
      return dateStr.trim();
    }

    // Try to parse ISO or other valid date formats
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      // Use UTC methods to avoid timezone shifting
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const year = date.getUTCFullYear();
      console.warn("Formatted date:", month, year);
      return `${month}/${year}`;
    }

    // Otherwise return as is
    return dateStr;
  }

  const handleReject = async () => {
    if (!selectedUpdate || !adminComment.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/profile-versions/${selectedUpdate.id}/reject`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ adminComment }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to reject profile update");
      }

      setIsModalOpen(false);
      setSelectedUpdate(null);
      fetchProfileUpdates();
    } catch (error) {
      console.error("Error rejecting profile update:", error);
      alert("Failed to reject profile update");
    } finally {
      setActionLoading(false);
    }
  };

  const parseSnapshot = (snapshot) => {
    if (!snapshot) return null;
    try {
      if (typeof snapshot === "string") {
        return snapshot.trim() ? JSON.parse(snapshot) : null;
      }
      if (typeof snapshot === "object") {
        return snapshot;
      }
      return null;
    } catch (error) {
      console.error("Failed to parse snapshot payload:", error);
      return null;
    }
  };

  const renderDiffRow = (label, previousValue, newValue) => {
    const safePrev = previousValue ?? "";
    const safeNew = newValue ?? "";
    const same = String(safePrev).trim() === String(safeNew).trim();

    return (
      <div className={`diff-row ${same ? "unchanged" : "changed"}`}>
        <div className="diff-label">{label}</div>
        <div className="diff-before" aria-label={`${label} previous value`}>
          <span className="diff-tag before">Previous</span>
          {safePrev || "Not specified"}
        </div>
        <div className="diff-after" aria-label={`${label} requested value`}>
          <span className="diff-tag after">Requested</span>
          {safePrev && !safeNew ? "Removed" : safeNew || "Not specified"}
        </div>
      </div>
    );
  };

  const renderNormalized = (previousNorm, requestedNorm) => {
    if (!previousNorm && !requestedNorm) {
      return <div className="error">No normalized data to display.</div>;
    }

    const prevPI = previousNorm?.personal_info || {};
    const reqPI = requestedNorm?.personal_info || {};
    const prevLangs = Array.isArray(previousNorm?.languages)
      ? previousNorm.languages
      : [];
    const reqLangs = Array.isArray(requestedNorm?.languages)
      ? requestedNorm.languages
      : [];
    const prevWorkCountries = Array.isArray(
      previousNorm?.work_eligible_countries,
    )
      ? previousNorm.work_eligible_countries
      : [];
    const reqWorkCountries = Array.isArray(
      requestedNorm?.work_eligible_countries,
    )
      ? requestedNorm.work_eligible_countries
      : [];
    const prevCD = previousNorm?.career_details || {};
    const reqCD = requestedNorm?.career_details || {};
    const prevRoles = Array.isArray(previousNorm?.preferred_roles)
      ? previousNorm.preferred_roles
      : [];
    const reqRoles = Array.isArray(requestedNorm?.preferred_roles)
      ? requestedNorm.preferred_roles
      : [];

    const toList = (value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === "string" && value.trim()) {
        return value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }
      return [];
    };

    const renderDiffList = (label, prevList, reqList) => {
      const prevDisplay = joinList(prevList);
      const reqDisplay =
        Array.isArray(reqList) && reqList.length
          ? reqList.join(", ")
          : Array.isArray(prevList) && prevList.length
            ? "Removed"
            : "Not specified";

      return renderDiffRow(label, prevDisplay, reqDisplay);
    };



    const yesNo = (v) =>
      v === 1
        ? "Yes"
        : v === 0
          ? "No"
          : v === true
            ? "Yes"
            : v === false
              ? "No"
              : "Not specified";
    const isEmpty = (v) =>
      v === null ||
      v === undefined ||
      (typeof v === "string" && v.trim() === "") ||
      (Array.isArray(v) && v.length === 0);
    const joinList = (arr) =>
      Array.isArray(arr) && arr.length ? arr.join(", ") : "Not specified";
    const fmtNotice = (v, u) => {
      const unit = cap(u);
      if (v === 0) return "Available immediately";
      return v ? `${v} ${unit || ""}`.trim() : "Not specified";
    };

    const pick = (prev, req) => (!isEmpty(req) ? req : prev);
    const roleFunctionName = (role) =>
      role?.function_name ?? role?.function ?? null;
    const roleRoleName = (role) => role?.role_name ?? role?.role ?? null;

    const roleEmploymentType = (role) => {
      const types = role?.employment_type ?? role?.employmentType ?? [];
      return Array.isArray(types) ? types.join(", ") : types || null;
    };
    const roleLocationType = (role) =>
      role?.location_type ?? role?.locationType ?? null;
    const roleRelocate = (role) =>
      role?.will_relocate ?? role?.willRelocate ?? null;
    const workSetupOptions = [
      { id: 1, name: "On-site" },
      { id: 2, name: "Hybrid" },
      { id: 3, name: "Remote" },
    ];

    const roleWorkSetup = (role) => {
      if (!role) return [];
      const value = role.work_setup ?? role.workSetup;

      if (Array.isArray(value)) {
        // Handle array of IDs or objects
        return value
          .map((item) => {
            if (typeof item === "number") {
              const match = workSetupOptions.find((opt) => opt.id === item);
              return match ? match.name : null;
            }
            if (typeof item === "object" && item !== null) {
              return (
                item.name ??
                workSetupOptions.find((opt) => opt.id === item.id)?.name ??
                null
              );
            }
            return item;
          })
          .filter(Boolean);
      }

      if (typeof value === "string") {
        // Handle comma-separated string
        return value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [];
    };

    const formatSetup = (role) => {
      const values = roleWorkSetup(role);
      return values.length ? values.join(", ") : "Not specified";
    };

    const alignByIdOrName = (prevItems = [], reqItems = []) => {
      const result = [];
      const reqMap = new Map();

      reqItems.forEach((item) => {
        const key =
          item.id ||
          item.institutionName ||
          item.role_name ||
          item.certificationName;
        if (key) reqMap.set(String(key).toLowerCase(), item);
      });

      const usedKeys = new Set();

      // Existing items (modified or removed)
      prevItems.forEach((prev) => {
        const key =
          prev.id ||
          prev.institution_name ||
          prev.role_name ||
          prev.certification_name;
        const matchKey = key ? String(key).toLowerCase() : null;
        const matchedReq =
          matchKey && reqMap.has(matchKey) ? reqMap.get(matchKey) : null;

        if (matchKey) usedKeys.add(matchKey);
        result.push({ prev, req: matchedReq || null });
      });

      // Newly added items
      reqItems.forEach((req) => {
        const key =
          req.id ||
          req.institutionName ||
          req.role_name ||
          req.certificationName;
        const matchKey = key ? String(key).toLowerCase() : null;
        if (!usedKeys.has(matchKey)) result.push({ prev: null, req });
      });

      return result;
    };

    const alignSkills = (prevInput = [], reqInput = []) => {
      const prevSkills = Array.isArray(prevInput)
        ? prevInput
        : Object.entries(prevInput || {}).map(([name, data]) => ({
          [name]: data,
        }));
      const reqSkills = Array.isArray(reqInput)
        ? reqInput
        : Object.entries(reqInput || {}).map(([name, data]) => ({
          [name]: data,
        }));

      const result = [];
      const reqMap = new Map();

      // Build map of requested skills by skill name
      reqSkills.forEach((skillObj) => {
        const [key, value] = Object.entries(skillObj || {})[0] || [];
        if (key) reqMap.set(key.toLowerCase(), { [key]: value });
      });

      const used = new Set();

      // Existing skills (modified or removed)
      prevSkills.forEach((skillObj) => {
        const [key, value] = Object.entries(skillObj || {})[0] || [];
        const matched = key ? reqMap.get(key.toLowerCase()) : null;
        if (key) used.add(key.toLowerCase());
        result.push({ prev: { [key]: value }, req: matched || null });
      });

      // Newly added skills
      reqSkills.forEach((skillObj) => {
        const [key, value] = Object.entries(skillObj || {})[0] || [];
        if (key && !used.has(key.toLowerCase())) {
          result.push({ prev: null, req: { [key]: value } });
        }
      });

      return result;
    };

    return (
      <div className="candidate-details-content">
        <div className="detail-section">
          <h3>Personal Information</h3>
          {renderDiffRow("First Name", prevPI.first_name, reqPI.first_name)}
          {renderDiffRow("Last Name", prevPI.last_name, reqPI.last_name)}
          {renderDiffRow("Email", prevPI.email, reqPI.email)}
          {/* {renderDiffRow(
            "Country",
            prevPI.country_name || prevPI.country_code,
            reqPI.country_name || reqPI.country_code
          )} */}

          {renderDiffRow(
            "Work eligibility for Switzerland:",
            prevPI?.workEligibleSwiss && prevPI?.workEligibleSwiss.length > 0
              ? prevPI?.workEligibleSwiss[0]
              : "Not specified",
            reqPI?.workEligibleSwiss && reqPI?.workEligibleSwiss.length > 0
              ? reqPI?.workEligibleSwiss[0]
              : "Not specified",
          )}

          {/* {renderDiffRow("City", prevPI.city, reqPI.city)} */}
          {renderDiffRow(
            "Job Search Status",
            prevPI.job_search_status,
            reqPI.job_search_status,
          )}
          {renderDiffRow(
            "Current Role",
            prevPI.current_role,
            reqPI.current_role,
          )}
          {renderDiffRow(
            "Notice Period",
            fmtNotice(prevPI.notice_period_value, prevPI.notice_period_unit),
            fmtNotice(
              pick(prevPI.notice_period_value, reqPI.notice_period_value),
              pick(prevPI.notice_period_unit, reqPI.notice_period_unit),
            ),
          )}


        </div>

        <div className="detail-section">
          <h3>Work Eligibility</h3>
          {renderDiffRow(
            "Eligible Countries",
            joinList(prevWorkCountries),
            joinList(reqWorkCountries),
          )}
        </div>

        <div className="detail-section">
          <h3>Languages</h3>
          {renderDiffRow(
            "Languages",
            joinList(prevLangs.map((l) => `${l.name} (${l.fluency})`)),
            joinList(reqLangs.map((l) => `${l.name} (${l.fluency})`)),
          )}
        </div>

        <div className="detail-section">
          <h3>Soft Skills</h3>
          {(() => {
            const prevSoftSkills = Array.isArray(prevPI?.softSkills)
              ? prevPI.softSkills
              : [];
            const reqSoftSkills = Array.isArray(reqPI?.softSkills)
              ? reqPI.softSkills
              : [];

            // Helper to align soft skills by ID or name
            const alignSoftSkills = (prevSkills = [], reqSkills = []) => {
              const result = [];
              const reqMap = new Map();

              reqSkills.forEach((item) => {
                const key =
                  item.softSkillId || item.id || item.softSkill || item.name;
                if (key) reqMap.set(String(key).toLowerCase(), item);
              });

              const usedKeys = new Set();

              // Existing (modified or removed)
              prevSkills.forEach((item) => {
                const key =
                  item.softSkillId || item.id || item.softSkill || item.name;
                const matchKey = key ? String(key).toLowerCase() : null;
                const matchedReq =
                  matchKey && reqMap.has(matchKey)
                    ? reqMap.get(matchKey)
                    : null;

                if (matchKey) usedKeys.add(matchKey);
                result.push({ prev: item, req: matchedReq || null });
              });

              // Newly added
              reqSkills.forEach((item) => {
                const key =
                  item.softSkillId || item.id || item.softSkill || item.name;
                const matchKey = key ? String(key).toLowerCase() : null;
                if (matchKey && !usedKeys.has(matchKey)) {
                  result.push({ prev: null, req: item });
                }
              });

              return result;
            };

            const alignedSoftSkills = alignSoftSkills(
              prevSoftSkills,
              reqSoftSkills,
            );

            if (!alignedSoftSkills.length) {
              return <div className="detail-row">No soft skills specified</div>;
            }

            return alignedSoftSkills.map(({ prev, req }, idx) => (
              <div key={idx} className="role-item">
                {renderDiffRow(
                  "Soft Skill",
                  prev?.softSkill || prev?.name || "Not specified",
                  req?.softSkill ||
                  req?.name ||
                  (prev ? "Removed" : "Not specified"),
                )}
                {renderDiffRow(
                  "Example",
                  prev?.evidence || "Not specified",
                  req?.evidence || (prev ? "Removed" : "Not specified"),
                )}
              </div>
            ));
          })()}
        </div>

        <div className="detail-section">
          {(() => {
            const beforeEmp = roleEmploymentType(prevCD);
            const afterEmp = reqCD
              ? roleEmploymentType(reqCD)
              : prevCD
                ? "Removed"
                : null;

            const beforeRel = roleRelocate(prevCD);
            const afterRel = reqCD
              ? roleRelocate(reqCD)
              : prevCD
                ? "Removed"
                : "Not specified";

            const beforeSetup = formatSetup(prevCD);
            const afterSetup = reqCD
              ? formatSetup(reqCD)
              : prevCD
                ? "Removed"
                : "Not specified";

            return (
              <>
                <h3>Career Details</h3>
                {renderDiffRow(
                  "Current Company",
                  prevCD.companyName,
                  reqCD.companyName || (prevCD.companyName ? "Removed" : null),
                )}
                {renderDiffRow(
                  "Current Position",
                  prevCD.current_position,
                  reqCD.current_position,
                )}
                {renderDiffRow(
                  "LinkedIn URL",
                  prevCD.linkedin_url,
                  reqCD.linkedin_url,
                )}
                <div className={`diff-row ${(prevCD.cv_filename || '') === (reqCD.cv_filename || '') ? "unchanged" : "changed"}`}>
                  <div className="diff-label">CV Filename</div>
                  <div className="diff-before" aria-label="CV Filename previous value">
                    <span className="diff-tag before">Previous</span>
                    {prevCD.cv_filename || "Not specified"}
                  </div>
                  <div className="diff-after" aria-label="CV Filename requested value">
                    <span className="diff-tag after">Requested</span>
                    {reqCD.cv_filename || (prevCD.cv_filename ? "Removed" : "Not specified")}
                    {reqCD.cv_filename &&
                      selectedUpdate?.user_id &&
                      prevCD.cv_filename !== reqCD.cv_filename && (
                        <button
                          className="view-cv-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewNewCV(selectedUpdate.user_id, reqCD.cv_filename);
                          }}
                        >
                          View New CV
                        </button>
                      )}
                  </div>
                </div>
                {renderDiffRow("Employment Type", beforeEmp, afterEmp)}
                {renderDiffRow("Open to Relocate", beforeRel, afterRel)}
                {renderDiffRow("Work Setup", beforeSetup, afterSetup)}
              </>
            );
          })()}
        </div>

        <div className="detail-section">
          <h3>Education</h3>
          {alignByIdOrName(prevCD.educations, reqCD.educations).map(
            ({ prev, req }, i) => (
              <div key={i} className="role-item">
                {renderDiffRow(
                  "Degree",
                  prev?.degree,
                  req?.degree || (prev?.degree ? "Removed" : null),
                )}
                {renderDiffRow(
                  "Field of Study",
                  prev?.field_of_study,
                  req?.fieldOfStudy ||
                  (prev?.field_of_study ? "Removed" : null),
                )}

                {renderDiffRow(
                  "Location",
                  prev?.location || "Not specified",
                  req?.location ||
                  (prev?.location ? "Removed" : "Not specified"),
                )}
              </div>
            ),
          )}
        </div>

        <div className="detail-section">
          <h3>Certifications</h3>
          {prevCD.certifications.length === 0 &&
            reqCD.certifications.length === 0 && (
              <div className="detail-row">No certifications specified</div>
            )}
          {alignByIdOrName(prevCD.certifications, reqCD.certifications).map(
            ({ prev, req }, i) => (
              <div key={i} className="role-item">
                {renderDiffRow(
                  "Certification Name",
                  prev?.certification_name,
                  req?.certificationName || (prev ? "Removed" : null),
                )}
                {renderDiffRow(
                  "Organization",
                  prev?.issuing_organization,
                  req?.issuingOrganization ||
                  (prev?.issuing_organization ? "Removed" : null),
                )}
                {renderDiffRow(
                  "Issue Date",
                  formatDate(prev?.issue_date),
                  formatDate(req?.issueDate) ||
                  (prev?.issue_date ? "Removed" : null),
                )}

                {renderDiffRow(
                  "Expiry Date",
                  formatDate(prev?.expiry_date || prev?.expiryDate) ||
                  (prev?.noExpiry || prev?.no_expiry
                    ? "No Expiry"
                    : "Not specified"),
                  formatDate(req?.expiryDate) ||
                  (req?.noExpiry || req?.no_expiry
                    ? "No Expiry"
                    : prev?.expiry_date
                      ? "Removed"
                      : "Not specified"),
                )}
                {renderDiffRow(
                  "Credential ID",
                  prev?.credential_id || prev?.credentialId || "Not specified",
                  req?.credentialId ||
                  (prev?.credential_id || prev?.credentialId
                    ? "Removed"
                    : "Not specified"),
                )}
                {renderDiffRow(
                  "Credential URL",
                  prev?.credential_url ||
                  prev?.credentialUrl ||
                  "Not specified",
                  req?.credentialUrl ||
                  (prev?.credential_url || prev?.credentialUrl
                    ? "Removed"
                    : "Not specified"),
                )}
              </div>
            ),
          )}
        </div>

        <div className="detail-section">
          <h3>Preferred Roles</h3>
          {alignByIdOrName(prevRoles, reqRoles).map(({ prev, req }, i) => (
            <div key={i} className="role-item">
              {renderDiffRow(
                "Function",
                prev?.function_name,
                req?.function_name || (prev ? "Removed" : null),
              )}
              {renderDiffRow(
                "Role",
                prev?.role_name,
                req?.role_name || (prev ? "Removed" : null),
              )}

              <div className="sub-section">
                <h4>Skills</h4>
                {alignSkills(prev?.skills, req?.skills).map(
                  ({ prev: ps, req: rs }, j) => {
                    const [pCat, pData] = ps
                      ? Object.entries(ps)[0]
                      : [null, null];
                    const [rCat, rData] = rs
                      ? Object.entries(rs)[0]
                      : [null, null];

                    return (
                      <div key={j} className="skill-item">
                        {renderDiffRow(
                          "Skill",
                          pCat,
                          rCat || (ps ? "Removed" : "Not s"),
                        )}
                        {renderDiffRow(
                          "Level",
                          pData?.level
                            ? pData.level.charAt(0).toUpperCase() +
                            pData.level.slice(1).toLowerCase()
                            : null,
                          rData?.level
                            ? rData.level.charAt(0).toUpperCase() +
                            rData.level.slice(1).toLowerCase()
                            : pData?.level
                              ? "Removed"
                              : null,
                        )}
                        {renderDiffRow(
                          "Sub Skills",
                          (pData?.subSkills || [])
                            .map((sub) =>
                              typeof sub === "string"
                                ? sub
                                : `${sub.name} (${sub.level})`,
                            )
                            .join(", "),
                          (rData?.subSkills || [])
                            .map((sub) =>
                              typeof sub === "string"
                                ? sub
                                : `${sub.name} (${sub.level})`,
                            )
                            .join(", ") ||
                          (pData?.subSkills?.length > 0 ? "Removed" : null),
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSnapshot = (snapshot, currentSnapshot) => {
    const data = parseSnapshot(snapshot);
    const current = parseSnapshot(currentSnapshot);

    if (!data) {
      return (
        <div className="error">Unable to load profile update details.</div>
      );
    }

    const personalInfo = data.personal_info || {};
    const currentPersonalInfo = current?.profile || {};
    const careerDetails = data.career_details || {};
    const currentCareerDetails = current?.careerDetails || {};
    const preferredRoles = Array.isArray(data.preferred_roles)
      ? data.preferred_roles
      : [];
    const currentPreferredRoles = Array.isArray(current?.preferredRoles)
      ? current.preferredRoles
      : [];
    const roleExpertise = Array.isArray(data.role_expertise)
      ? data.role_expertise
      : [];
    const currentRoleExpertise = Array.isArray(current?.roleExpertise)
      ? current.roleExpertise
      : [];
    const languages = Array.isArray(personalInfo.languages)
      ? personalInfo.languages
      : [];
    const currentLanguages = Array.isArray(current?.languages)
      ? current.languages
      : [];
    const workCountries = Array.isArray(personalInfo.workEligibleCountryNames)
      ? personalInfo.workEligibleCountryNames
      : Array.isArray(data.workEligibleCountries)
        ? data.workEligibleCountries
        : Array.isArray(personalInfo.workEligibleCountries)
          ? personalInfo.workEligibleCountries
          : [];
    const currentWorkCountries = Array.isArray(current?.workEligibleCountries)
      ? current.workEligibleCountries.map((c) => c.name || c.code || c)
      : [];

    const formatNoticePeriod = () => {
      const notice = personalInfo.noticePeriod;
      if (!notice || (!notice.value && notice.value !== 0))
        return "Not specified";
      return `${notice.value} ${cap(notice.unit) || ""}`.trim();
    };

    const formatRoleField = (role, key, fallbackKey) => {
      if (!role) return "Not specified";
      return role[key] || role[fallbackKey] || "Not specified";
    };

    const resolveRelocation = (value) => {
      if (value === true || value === 1 || value === "Yes") return "Yes";
      if (value === false || value === 0 || value === "No") return "No";
      return "Not specified";
    };

    const toList = (value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === "string" && value.trim()) {
        return value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }
      return [];
    };

    const currentCompanyTypes = toList(
      currentPersonalInfo.preferred_company_types ??
      currentPersonalInfo.preferredCompanyTypes ??
      currentPersonalInfo.company_types,
    );
    const requestedCompanyTypes = toList(
      personalInfo.preferred_company_types ??
      personalInfo.preferredCompanyTypes ??
      personalInfo.company_types,
    );
    const currentWorkEnvironments = toList(
      currentPersonalInfo.preferred_work_environments ??
      currentPersonalInfo.preferredWorkEnvironments ??
      currentPersonalInfo.work_environments,
    );
    const requestedWorkEnvironments = toList(
      personalInfo.preferred_work_environments ??
      personalInfo.preferredWorkEnvironments ??
      personalInfo.work_environments,
    );

    return (
      <div className="candidate-details-content">
        <div className="detail-section">
          <h3>Personal Information</h3>
          {renderDiffRow(
            "First Name",
            currentPersonalInfo.first_name || currentPersonalInfo.firstName,
            personalInfo.firstName || personalInfo.first_name,
          )}
          {renderDiffRow(
            "Last Name",
            currentPersonalInfo.last_name || currentPersonalInfo.lastName,
            personalInfo.lastName || personalInfo.last_name,
          )}
          {renderDiffRow(
            "Email",
            currentPersonalInfo.email,
            personalInfo.email,
          )}
          {renderDiffRow(
            "Country",
            currentPersonalInfo.country_name ||
            currentPersonalInfo.country_code,
            personalInfo.countryName || personalInfo.country,
          )}
          {/* {renderDiffRow("City", currentPersonalInfo.city, personalInfo.city)} */}
          {renderDiffRow(
            "Job Search Status",
            currentPersonalInfo.job_search_status,
            personalInfo.jobSearchStatus || personalInfo.job_search_status,
          )}
          {renderDiffRow(
            "Current Industry",
            currentPersonalInfo.current_industry,
            personalInfo.currentIndustry || personalInfo.function,
          )}
          {renderDiffRow(
            "Current Role",
            currentPersonalInfo.current_role,
            personalInfo.currentRole || personalInfo.current_role,
          )}
          {renderDiffRow(
            "Notice Period",
            currentPersonalInfo.notice_period_value
              ? `${currentPersonalInfo.notice_period_value} ${cap(
                currentPersonalInfo.notice_period_unit,
              )}`
              : "Not specified",
            formatNoticePeriod(),
          )}
          {renderDiffRow(
            "Languages",
            currentLanguages.length
              ? currentLanguages
                .map((lang) => `${lang.name} (${lang.fluency})`)
                .join(", ")
              : "Not specified",
            languages.length
              ? languages
                .map((lang) => `${lang.name} (${lang.fluency})`)
                .join(", ")
              : "Not specified",
          )}
          {renderDiffRow(
            "Preferred Company Types",
            currentCompanyTypes.length
              ? currentCompanyTypes.join(", ")
              : "Not specified",
            requestedCompanyTypes.length
              ? requestedCompanyTypes.join(", ")
              : "Not specified",
          )}
          {renderDiffRow(
            "Preferred Work Environment",
            currentWorkEnvironments.length
              ? currentWorkEnvironments.join(", ")
              : "Not specified",
            requestedWorkEnvironments.length
              ? requestedWorkEnvironments.join(", ")
              : "Not specified",
          )}
        </div>

        <div className="detail-section">
          <h3>Work Eligibility</h3>
          {renderDiffRow(
            "Eligible Countries",
            currentWorkCountries.length
              ? currentWorkCountries.join(", ")
              : "Not specified",
            workCountries.length ? workCountries.join(", ") : "Not specified",
          )}
        </div>

        <div className="detail-section">
          <h3>Career Details</h3>
          {renderDiffRow(
            "LinkedIn URL",
            currentCareerDetails.linkedin_url,
            careerDetails.linkedin_url,
          )}
          {renderDiffRow(
            "Current Position",
            currentCareerDetails.current_position,
            careerDetails.current_position,
          )}
          <div className={`diff-row ${(currentCareerDetails.cv_filename || '') === (data.cv_file?.originalname || '') ? "unchanged" : "changed"}`}>
            <div className="diff-label">CV Filename</div>
            <div className="diff-before" aria-label="CV Filename previous value">
              <span className="diff-tag before">Previous</span>
              {currentCareerDetails.cv_filename || "Not specified"}
            </div>
            <div className="diff-after" aria-label="CV Filename requested value">
              <span className="diff-tag after">Requested</span>
              {data.cv_file?.originalname || (currentCareerDetails.cv_filename ? "Removed" : "Not specified")}
              {data.cv_file?.originalname &&
                selectedUpdate?.user_id &&
                currentCareerDetails.cv_filename !== data.cv_file?.originalname && (
                  <button
                    className="view-cv-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewNewCV(selectedUpdate.user_id, data.cv_file.originalname);
                    }}
                  >
                    View New CV
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="container">
          <div className="loading-spinner">Loading profile updates...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="container">
        <div className="page-header">
          <Link to="/" className="back-link">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="page-title">Approve Profile Updates</h1>
          {/* <p className="page-subtitle">
            Track and review profile change requests.
          </p> */}
          <div className="view-toggle" role="tablist" aria-label="View mode">
            <button
              type="button"
              className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
              role="tab"
              aria-selected={viewMode === "table"}
            >
              Table
            </button>
            <button
              type="button"
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              role="tab"
              aria-selected={viewMode === "grid"}
            >
              Grid
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {stats && (
          <div className="overview-grid">
            <div className="overview-card">
              <span className="overview-label">Pending requests</span>
              <strong className="overview-value">{stats.total}</strong>
            </div>
            <div className="overview-card">
              <span className="overview-label">Latest submission</span>
              <strong className="overview-value">
                {stats.latestSubmittedAt}
              </strong>
            </div>
            <div className="overview-card">
              <span className="overview-label">Oldest request</span>
              <strong className="overview-value">
                {stats.longestPendingAge} day(s)
              </strong>
            </div>
          </div>
        )}

        {profileUpdates.length === 0 ? (
          <div className="empty-state">
            <h3>No pending profile updates</h3>
            <p>
              There are currently no profile update requests awaiting review.
            </p>
          </div>
        ) : (
          <>
            {viewMode === "table" ? (
              <div className="table-wrap">
                <table className="candidates-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Version</th>
                      <th>Submitted</th>
                      <th>Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profileUpdates.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <div className="cell-user">
                            <div className="avatar-sm">
                              <span>{u.first_name?.[0] || "U"}</span>
                            </div>
                            <div className="cell-user-meta">
                              <div className="name">
                                {u.first_name && u.last_name
                                  ? `${u.first_name} ${u.last_name}`
                                  : "Unnamed Candidate"}
                              </div>
                              <div className="email">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {(() => {
                            const raw = (
                              u.review_status || "Pending"
                            ).toString();
                            const cls = raw
                              .toLowerCase()
                              .replace(/\s+/g, "-")
                              .replace(/[^a-z-]/g, "");
                            return (
                              <span
                                className={`status-pill ${cls || "pending"}`}
                              >
                                {raw}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          <span className="badge-soft">
                            V{u.version_number}
                          </span>
                        </td>
                        <td>
                          {new Date(u.created_at).toLocaleDateString()}{" "}
                          <span className="muted">
                            {new Date(u.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </td>
                        <td className="decision-cell">
                          <button
                            className="link-action"
                            onClick={() => handleViewDetails(u)}
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="candidates-grid">
                {profileUpdates.map((update) => (
                  <div key={update.id} className="candidate-card">
                    <div className="candidate-header">
                      <div className="candidate-avatar">
                        <span>{update.first_name?.charAt(0) || "U"}</span>
                      </div>
                      <div className="candidate-info">
                        <h3>
                          {update.first_name && update.last_name
                            ? `${update.first_name} ${update.last_name}`
                            : "Unnamed Candidate"}
                        </h3>
                        <p>{update.email}</p>
                      </div>
                      <div className="badge-soft">V{update.version_number}</div>
                    </div>

                    <div className="candidate-details">
                      <div className="candidate-meta">
                        <div className="meta-item">
                          <span className="meta-label">Submitted</span>
                          <span className="meta-value">
                            {new Date(update.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Status</span>
                          {/* <span className="meta-badge">
                            {update.review_status}
                          </span> */}
                          {(() => {
                            const raw = (
                              update.review_status || "Pending"
                            ).toString();
                            const cls = raw
                              .toLowerCase()
                              .replace(/\s+/g, "-")
                              .replace(/[^a-z-]/g, "");
                            return (
                              <span className="meta-badge">
                                <span
                                  className={`status-pill ${cls || "pending"}`}
                                >
                                  {raw}
                                </span>
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    <div className="candidate-actions">
                      <button
                        className="link-action"
                        onClick={() => handleViewDetails(update)}
                      >
                        Review
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {isModalOpen && selectedUpdate && (
          <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h2>Profile update</h2>
                  <p className="modal-subtitle">
                    Review the requested changes against the current profile.
                  </p>
                </div>
                <button
                  className="modal-close"
                  onClick={() => setIsModalOpen(false)}
                  aria-label="Close dialog"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="modal-body">
                <div className="modal-summary">
                  <div>
                    <span className="summary-label">Candidate</span>
                    <span className="summary-value">
                      {selectedUpdate.first_name} {selectedUpdate.last_name}
                    </span>
                  </div>
                  <div>
                    <span className="summary-label">Email</span>
                    <span className="summary-value">
                      {selectedUpdate.email}
                    </span>
                  </div>
                  <div>
                    <span className="summary-label">Version</span>
                    <span className="summary-value">
                      {selectedUpdate.version_number}
                    </span>
                  </div>
                  <div>
                    <span className="summary-label">Submitted</span>
                    <span className="summary-value">
                      {new Date(selectedUpdate.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="modal-divider" />
                {detailsLoading ? (
                  <div className="loading">Loading version details...</div>
                ) : (
                  (() => {
                    const prevNorm =
                      versionDetails?.previous_normalized ??
                      selectedUpdate.previous_normalized;
                    const reqNorm =
                      versionDetails?.requested_normalized ??
                      selectedUpdate.requested_normalized;
                    if (prevNorm || reqNorm) {
                      return renderNormalized(prevNorm, reqNorm);
                    }
                    return renderSnapshot(
                      versionDetails?.snapshot ?? selectedUpdate.snapshot,
                      versionDetails?.current_snapshot ??
                      selectedUpdate.current_snapshot,
                    );
                  })()
                )}
                <div className="modal-divider" />

                <div className="admin-comment-section">
                  <label htmlFor="adminComment">Reviewer notes</label>
                  <p className="comment-hint">
                    Provide context for the candidate. A note is required when
                    rejecting an update.
                  </p>
                  <textarea
                    id="adminComment"
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    placeholder="Share your decision rationale..."
                    rows="4"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="reject-btn"
                  onClick={handleReject}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Processing..." : "Reject"}
                </button>
                <button
                  className="approve-btn"
                  onClick={handleApprove}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Processing..." : "Approve"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PDF/DOCX Viewer Modal */}
        {showPdfViewer && (
          <div className="pdf-viewer-overlay" onClick={handleClosePdfViewer}>
            <div className="pdf-viewer-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pdf-viewer-header">
                <h3>CV Preview</h3>
                <button
                  className="pdf-viewer-close"
                  onClick={handleClosePdfViewer}
                  aria-label="Close PDF viewer"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="pdf-viewer-content">
                {pdfLoading ? (
                  <div className="pdf-loading">Loading CV...</div>
                ) : cvType === "docx" && currentBlob ? (
                  <>
                    {console.log("Rendering DOCX viewer, cvType:", cvType)}
                    <div
                      ref={docxRef}
                      className="docx-viewer"
                      style={{
                        width: "100%",
                        minHeight: "500px",
                        backgroundColor: "#fff",
                      }}
                    />
                  </>
                ) : cvType === "pdf" && pdfUrl ? (
                  <>
                    <Document
                      file={pdfUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={(error) => {
                        console.error("Error loading PDF:", error);
                        // If PDF fails, fallback to DOCX rendering
                        console.log("PDF parsing failed, falling back to DOCX rendering...");
                        setCvType("docx");
                        setPdfLoading(false);
                      }}
                      loading={<div className="pdf-loading">Loading PDF...</div>}
                    >
                      <Page
                        pageNumber={pageNumber}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        width={Math.min(850, window.innerWidth * 0.85)}
                      />
                    </Document>
                    {numPages > 1 && (
                      <div className="pdf-pagination">
                        <button
                          onClick={goToPrevPage}
                          disabled={pageNumber <= 1}
                          className="page-btn"
                        >
                          Previous
                        </button>
                        <span className="page-info">
                          Page {pageNumber} of {numPages}
                        </span>
                        <button
                          onClick={goToNextPage}
                          disabled={pageNumber >= numPages}
                          className="page-btn"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                ) : cvType === "docx" && currentBlob ? (
                  <div
                    ref={docxRef}
                    className="docx-viewer"
                    style={{
                      width: "100%",
                      minHeight: "500px",
                      backgroundColor: "#fff",
                    }}
                  />
                ) : (
                  <div className="pdf-error">
                    <p>Unable to preview this file type.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApproveProfileUpdates;
